import { Injectable } from '@angular/core';
import { ActiveChannelDTO } from 'app/shared/models/channel.model';
import { Shop } from 'app/shared/models/shop.model';
import { UserInfo } from 'app/shared/models/user-info.model';
import { CoreService } from 'app/shared/services/core.service';
import { UtilityFunctions } from 'app/shared/utilities/functions.models';
import { environment } from 'environments/environment';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ContextService {

  public get shops(): Observable<Shop[]> { return this._shops.asObservable(); }
  public get shopsValue(): Shop[] { return this._shops.value; }
  public get currentShop(): Observable<Shop> { return this._currentShop.asObservable(); }
  public get currentShopValue(): Shop { return this._currentShop.value; }

  public get activatedChannels(): Observable<ActiveChannelDTO[]> { return this._activatedChannels.asObservable(); }
  public get activatedChannelsValue(): ActiveChannelDTO[] { return this._activatedChannels.value; }
  public get currentActivatedChannel(): Observable<ActiveChannelDTO> { return this._currentActivatedChannel.asObservable(); }
  public get currentActivatedChannelValue(): ActiveChannelDTO { return this._currentActivatedChannel.value; }

  public get currentUser(): Observable<UserInfo> { return this._user.asObservable(); }
  public get currentUserValue(): UserInfo { return this._user.value; }

  private _currentShop = new BehaviorSubject<Shop>(null);
  private _currentActivatedChannel = new BehaviorSubject<ActiveChannelDTO>(null);
  private _user = new BehaviorSubject<UserInfo>(null);
  private _shops = new BehaviorSubject<Shop[]>(null);
  private _activatedChannels = new BehaviorSubject<ActiveChannelDTO[]>(null);

  baseUrl: string;

  constructor(
    private _core: CoreService
  ) {
    this.baseUrl = environment.urls.apiUrl.url;
    this.channelsSubscribe();
    this.currentShopSubscribe();
    this.currentChannelSubscribe();
    this.currentUserSubscribe();
  }

  public getContext(loaderId: string): Observable<{ UserInfo: UserInfo, Stores: Shop[]; }> {
    const subject = new Subject<{ UserInfo: UserInfo, Stores: Shop[]; }>();
    const currentUser = this.currentUserValue;
    if (!currentUser) {
      forkJoin({
        UserInfo: this._core.getUserInfo(loaderId),
        Stores: this._core.getShops(),
      }).subscribe(data => {
        this._shops.next(data.Stores);
        subject.next({ UserInfo: data.UserInfo, Stores: data.Stores });
        if (data.Stores?.length) this.handleStoresAndSetCurrentShop(data.Stores);
      });
    } else {
      this._core.getShops().subscribe(shops => {
        this._shops.next(shops);
        subject.next({ UserInfo: currentUser, Stores: shops });
        if (shops?.length) this.handleStoresAndSetCurrentShop(shops);
      });
    }
    return subject.asObservable();
  }

  private channelsSubscribe() {
    this._core.channelsSubject.subscribe(channels => {
      this._activatedChannels.next(channels);
    });
  }

  public setChannels(channels: ActiveChannelDTO[]) {
    this._core.setChannels(channels);
  }

  private handleStoresAndSetCurrentShop(shops: Shop[]) {
    const shop = UtilityFunctions.getCurrentShopByUrl(shops);
    if (shop) this.setCurrentShop(shop);
  }

  public setCurrentShop(shop: Shop) {
    this._core.setCurrentShop(shop);
    this.setChannels(shop.activatedStoreChannels);
  }

  public getChannelsFromStore(storeId: number): ActiveChannelDTO[] | null {
    return this.shopsValue.find(x => x.id === storeId)?.activatedStoreChannels ?? null;
  }

  public getChannelById(channelId: number): ActiveChannelDTO {
    return this.activatedChannelsValue.find(x => x.id === channelId);
  }

  public getShopById(shopId: number): Shop {
    return this.shopsValue.find(x => x.id === shopId);
  }

  public updateUserInfo(userInfo: UserInfo) {
    this._user.next(userInfo);
  }

  public updateUserCulture(culture: number) {
    const userInfo = this._user.value;
    userInfo.culture = culture;
    this._user.next(userInfo);
  }

  public updateUserCurrentTenant(loaderId: string, tenantId: string) {
    return this._core.updateUserCurrentTenant(loaderId, tenantId);
  }

  private currentShopSubscribe() {
    this._core.currentShopChanged$.subscribe(data => {
      this._currentShop.next(data);
      if (data) document.documentElement.style.setProperty('--primary', this.rgbFromHex(data.primaryColor));
    });
  }

  private currentChannelSubscribe() {
    this._core.currentActivatedChannelChanged$.subscribe(channel => {
      this._currentActivatedChannel.next(channel);
    });
  }

  private currentUserSubscribe() {
    this._core.userInfoChanged$.subscribe(data => this._user.next(data));
  }

  private rgbFromHex(hex: string): string {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? parseInt(result[1], 16).toString() + ", " + parseInt(result[2], 16).toString() + ', ' + parseInt(result[3], 16).toString() : null;
    // return result ? "rgb(" + parseInt(result[1], 16).toString() + ", " + parseInt(result[2], 16).toString() + ', ' + parseInt(result[3], 16).toString() + ")" : null;
  }
}
